import { useState } from "react";
import { CableIcon } from "lucide-react";

import {
  useTransportSettings,
  useUpdateTenantTransport,
  useUpdateUserTransport,
  type Transport,
  type UserOverride,
} from "@/api/liveView";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/auth";
import { QueryState } from "@/components/QueryState";


export default function LiveViewTransportSettings() {
  const { data: settings, isLoading, isError, error, refetch } = useTransportSettings();
  const updateTenant = useUpdateTenantTransport();
  const updateUser = useUpdateUserTransport();
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === "admin";
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // T1a — `isLoading || !settings` renders "Loading…" FOREVER on a failed fetch:
  // React Query sets isLoading=false and settings=undefined, so this branch stays
  // true and nothing ever says the request failed. A spinner that never
  // resolves is the same false signal as an empty table.
  if (isError) {
    return (
      <QueryState
        isLoading={false}
        isError
        error={error}
        label="the live-view transport setting"
        onRetry={refetch}
      >
        {null}
      </QueryState>
    );
  }
  if (isLoading || !settings) {
    return <p className="text-sm">Loading…</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <CableIcon className="h-6 w-6" />
          Live View Mode
        </h1>
        <p className="text-sm text-muted-foreground">
          Choose between Compatible (works everywhere, 6–8 s delay)
          and Low latency (under 1 second). Set an organisation-wide
          default and an optional personal override.
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Current mode</h2>
            <Badge
              variant={settings.effective === "webrtc" ? "online" : "offline"}
            >
              {settings.effective === "webrtc"
                ? "Low latency"
                : "Compatible"}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            Resolved as <code>your preference → organisation default →
              system default (Compatible)</code>. Low latency falls back
            to Compatible automatically when your network can't support
            it (for example, restrictive firewalls).
          </p>
        </CardContent>
      </Card>

      {isAdmin && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-lg font-semibold">Tenant default</h2>
            <p className="text-xs text-muted-foreground">
              Applies to every user in this tenant who hasn't set their
              own override. Admin only.
            </p>
            <div className="space-y-1">
              <Label>Default mode</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={settings.tenant_default}
                onChange={async (e) => {
                  await updateTenant.mutateAsync(e.target.value as Transport);
                  setSavedAt(Date.now());
                }}
              >
                <option value="hls">Compatible (default)</option>
                <option value="webrtc">Low latency</option>
              </select>
            </div>
            {savedAt && (
              <p className="text-xs text-emerald-600">Saved.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-lg font-semibold">Personal override</h2>
          <p className="text-xs text-muted-foreground">
            Overrides the tenant default for your own browser
            sessions only.
          </p>
          <div className="space-y-1">
            <Label>My preference</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={settings.user_override}
              onChange={async (e) => {
                await updateUser.mutateAsync(e.target.value as UserOverride);
                setSavedAt(Date.now());
              }}
            >
              <option value="inherit">
                Use organisation default ({settings.tenant_default === "webrtc" ? "Low latency" : "Compatible"})
              </option>
              <option value="hls">Compatible</option>
              <option value="webrtc">Low latency</option>
            </select>
          </div>
          <div className="pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await updateUser.mutateAsync("inherit");
                setSavedAt(Date.now());
              }}
            >
              Reset to organisation default
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
